import { useContext, useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import { Box, Button, Drawer, Group, NumberInput, Stack } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { updateEncounterTimeSpent } from "@/encounters/services/encounter-time-spent";
import { EncounterTimeSpentInput } from "@/encounters/types/encounter-time-spent";
import {
  getEncounterTimeSpent,
  sumEncounterTimeSpentMinutes,
} from "@/encounters/utils/encounter-time-spent";
import {
  encounterTimeSpentFormSchema,
  MAX_TIME_SPENT_MINUTES,
} from "@/encounters/zod-schemas/encounter-time-spent";
import { toast } from "@/utils/notifications";

interface EncounterTimeSpentDrawerProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Drawer that edits the time spent on the encounter in `EncounterContext`. Total Time is read-only
 * and always the sum of the two inputs. A successful save replaces the encounter in context.
 */
export default function EncounterTimeSpentDrawer({
  opened,
  onClose,
}: EncounterTimeSpentDrawerProps) {
  const medplum = useMedplum();
  const { encounter, setEncounter } = useContext(EncounterContext);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<EncounterTimeSpentFormValues>({
    initialValues: EMPTY_FORM_VALUES,
    validate: zodResolver(encounterTimeSpentFormSchema),
  });

  useEffect(() => {
    if (!opened) return;

    const initialValues = toFormValues(getEncounterTimeSpent(encounter));
    form.setInitialValues(initialValues);
    form.setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const totalMinutes = sumEncounterTimeSpentMinutes(toInput(form.values));

  const handleSubmit = async (values: EncounterTimeSpentFormValues) => {
    if (!encounter?.id) {
      toast(SAVE_FAILED_TOAST);
      return;
    }

    setIsSaving(true);
    const result = await updateEncounterTimeSpent(medplum, encounter.id, toInput(values));
    setIsSaving(false);

    if (!result.ok) {
      toast(result.reason === "version_conflict" ? VERSION_CONFLICT_TOAST : SAVE_FAILED_TOAST);
      return;
    }

    setEncounter(result.data);
    toast({
      type: "success",
      title: "Time spent saved",
      message: "The time spent during this encounter was saved.",
      autoClose: 3000,
    });
    onClose();
  };

  return (
    <Drawer.Root size={512} offset={8} opened={opened} position="right" onClose={onClose}>
      <Drawer.Overlay />
      <Drawer.Content data-testid="encounter-time-spent-drawer">
        <form onSubmit={form.onSubmit(handleSubmit)} data-testid="encounter-time-spent-form">
          <Drawer.Header mb="lg" px="md" bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton size="xl" data-testid="encounter-time-spent-close-button" />
                <Drawer.Title c="dark.9">Time Spent During Encounter</Drawer.Title>
              </Group>
              <Button
                type="submit"
                loading={isSaving}
                data-testid="encounter-time-spent-save-button"
              >
                Save
              </Button>
            </Group>
          </Drawer.Header>
          <Stack px="lg" gap="lg">
            <NumberInput
              label="Face-to-Face w/ Client"
              {...MINUTES_INPUT_PROPS}
              data-testid="encounter-time-spent-face-to-face-input"
              {...form.getInputProps("faceToFaceMinutes")}
            />
            <NumberInput
              label="Coordinating Care"
              {...MINUTES_INPUT_PROPS}
              data-testid="encounter-time-spent-care-coordination-input"
              {...form.getInputProps("careCoordinationMinutes")}
            />
            <NumberInput
              readOnly
              label="Total Time"
              clampBehavior="strict"
              value={totalMinutes ?? ""}
              rightSection={<MinutesRightSection />}
              rightSectionWidth={MINUTES_SECTION_WIDTH}
              data-testid="encounter-time-spent-total-input"
            />
          </Stack>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}

const MinutesRightSection = () => (
  <Box
    h="100%"
    w="100%"
    bg="gray.1"
    display="flex"
    style={{
      borderLeft: "1px solid var(--mantine-color-gray-5)",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <TextWrapper size="md" c="gray.7">
      min
    </TextWrapper>
  </Box>
);

/**
 * Reads a NumberInput value as minutes. An empty input is "not recorded".
 *
 * @param value NumberInput value: a number, or `""` when empty.
 * @returns The minutes, or undefined when empty.
 */
function toMinutes(value: number | string): number | undefined {
  return typeof value === "number" ? value : undefined;
}

/**
 * Converts drawer form values to the service input.
 *
 * @param values Drawer form values.
 * @returns Face-to-face and care coordination minutes.
 */
function toInput(values: EncounterTimeSpentFormValues): EncounterTimeSpentInput {
  return {
    faceToFaceMinutes: toMinutes(values.faceToFaceMinutes),
    careCoordinationMinutes: toMinutes(values.careCoordinationMinutes),
  };
}

/**
 * Converts recorded time spent to drawer form values.
 *
 * @param values Recorded minutes.
 * @returns Form values, with `""` for unrecorded fields.
 */
function toFormValues(values: EncounterTimeSpentInput): EncounterTimeSpentFormValues {
  return {
    faceToFaceMinutes: values.faceToFaceMinutes ?? "",
    careCoordinationMinutes: values.careCoordinationMinutes ?? "",
  };
}

const MINUTES_SECTION_WIDTH = 74;

const MINUTES_INPUT_PROPS = {
  min: 0,
  max: MAX_TIME_SPENT_MINUTES,
  allowDecimal: false,
  allowNegative: false,
  clampBehavior: "strict",
  rightSectionWidth: MINUTES_SECTION_WIDTH,
  rightSection: <MinutesRightSection />,
} as const;

const SAVE_FAILED_TOAST = {
  type: "error",
  title: "Failed to save time spent",
  message: "The time spent during this encounter could not be saved. Try again.",
  autoClose: false,
} as const;

const VERSION_CONFLICT_TOAST = {
  type: "error",
  title: "Encounter changed while saving",
  message: "Someone else updated this encounter at the same time. Save the time spent again.",
  autoClose: false,
} as const;

const EMPTY_FORM_VALUES: EncounterTimeSpentFormValues = {
  faceToFaceMinutes: "",
  careCoordinationMinutes: "",
};

interface EncounterTimeSpentFormValues {
  faceToFaceMinutes: number | string;
  careCoordinationMinutes: number | string;
}
